import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, Facebook, Instagram, Mail, Menu, Phone, X, Youtube, MessageCircle  } from 'lucide-react';
import logo from '../assets/image/Logo-Bleen.png';
import { api } from '../api';

/* ─── nav data ───────────────────────────────────────────────── */
const navItems = [
  { label: 'Home', path: '/' },
  {
    label: 'Company',
    path: '/company',
    sub: [
      { label: 'Who We Are', path: '/company', hash: 'who-we-are' },
      { label: 'Why Us',     path: '/company', hash: 'why-us'     },
      { label: 'Innovation', path: '/company', hash: 'innovation' },
    ],
  },
  { label: 'Divisions',      path: '/divisions'      },
  { label: 'Products',       path: '/products'       },
  { label: 'Responsibility', path: '/responsibility' },
  { label: 'Blog',           path: '/blog'           },
  { label: 'Careers',        path: '/careers'        },
  { label: 'Contact',        path: '/contact'        },
  // { label: 'Bulk Import',    path: '/bulk-import'    }
];

const formatNavLabel = (value = '') =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/\b([a-z])/g, char => char.toUpperCase());

const socialLinks = [
  {
    label: 'Facebook',
    Icon: Facebook,
    url: 'https://www.facebook.com/bleencosmetics/followers/',
  },
  {
    label: 'Instagram',
    Icon: Instagram,
    url: 'https://www.instagram.com/bleenpharma/',
  },
  {
    label: 'WhatsApp',
    Icon: MessageCircle,
    url: 'https://wa.me/917373038882',
  },
];

function scrollToHash(hash, delay = 0) {
  const run = () => {
    const el = document.getElementById(hash);
    if (el) {
      const offset = 88;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };
  if (delay) setTimeout(run, delay);
  else run();
}

/* ─── Styles ─────────────────────────────────────────────────── */
const css = `
/* ── top bar ── */
.nb-topbar {
  background: linear-gradient(135deg, #2eb4b4 0%, #174f9a 100%);
  color: #fff;
  font-size: .8rem;
  padding: .45rem 1rem;
}
@media (max-width: 767px) { .nb-topbar { display: none; } }
.nb-topbar-inner {
  max-width: 1200px; margin: 0 auto;
  display: flex; justify-content: space-between; align-items: center; gap: 1rem;
}
.nb-tb-links { display: flex; align-items: center; gap: .9rem; flex-wrap: wrap; }
.nb-tb-link {
  display: flex; align-items: center; gap: .35rem;
  color: rgba(255,255,255,.88); text-decoration: none;
  transition: color .2s; white-space: nowrap;
  font-size: .78rem;
}
.nb-tb-link:hover { color: #fff; }
.nb-tb-socials { display: flex; align-items: center; gap: .35rem; flex-wrap: wrap; }
.nb-tb-social {
  width: 36px; height: 36px; border-radius: 50%;
  display: grid; place-items: center;
  color: rgba(255,255,255,.88); border: none; background: transparent; cursor: pointer;
  transition: background .2s, color .2s, transform .2s;
}
.nb-tb-social:hover { background: rgba(255,255,255,.15); color: #fff; transform: scale(1.08); }
@media (max-width: 1023px) {
  .nb-tb-social { width: 34px; height: 34px; }
}
@media (max-width: 767px) {
  .nb-topbar { font-size: .75rem; }
  .nb-tb-links { gap: .65rem; }
  .nb-tb-link { font-size: .72rem; }
  .nb-tb-social { width: 32px; height: 32px; }
}

/* ── nav shell ── */
.nb-shell {
  position: sticky;
  top: 0;
  /*
    CRITICAL: z-index must be BELOW the mobile drawer backdrop (z:9000)
    but above page content. z:1000 is correct here.
    The old code used z-index:100 which was BELOW some page elements.
  */
  z-index: 1000;
  background: rgba(255,255,255,.97);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border-bottom: 1px solid rgba(226,232,240,.8);
  transition: box-shadow .3s, background .3s;
}
.nb-shell.scrolled {
  box-shadow: 0 4px 24px rgba(23,79,154,.08);
  background: rgba(255,255,255,.99);
}
.nb-inner {
  max-width: 1200px; margin: 0 auto;
  padding: 0 1.25rem;
  display: flex; align-items: center; justify-content: space-between;
  min-height: 68px; gap: 1rem;
}

/* logo */
.nb-logo {
  display: flex; align-items: center; gap: .75rem;
  text-decoration: none; flex-shrink: 0; padding: .5rem 0;
}
.nb-logo img { height: 42px; width: auto; object-fit: contain; }
.nb-logo-text {
  font-size: .65rem; line-height: 1.3; color: #64748b; font-weight: 600;
  border-left: 1px solid #e2e8f0; padding-left: .65rem;
}
@media (max-width: 480px) { .nb-logo-text { display: none; } }

/* desktop links */
.nb-links {
  display: flex; align-items: center; gap: .25rem;
  flex: 1; justify-content: center;
}
@media (max-width: 1023px) { .nb-links { display: none; } }

.nb-item { position: relative; }

.nb-link {
  display: flex; align-items: center; gap: .3rem;
  padding: .55rem .9rem; border-radius: 99px;
  font-size: .82rem; font-weight: 600; color: #475569;
  text-decoration: none; cursor: pointer; position: relative;
  transition: color .2s, background .2s;
  white-space: nowrap; border: none; background: transparent;
}
.nb-link:hover  { color: #0f766e; background: rgba(46,180,180,.07); }
.nb-link.active { color: #0f766e; }
.nb-link .nb-chevron { transition: transform .25s cubic-bezier(.16,1,.3,1); }
.nb-link .nb-chevron.open { transform: rotate(180deg); }
.nb-link::after {
  content: ''; position: absolute;
  bottom: 6px; left: 50%; transform: translateX(-50%);
  width: 0; height: 2px; border-radius: 1px;
  background: linear-gradient(90deg, #2eb4b4, #174f9a);
  transition: width .28s cubic-bezier(.16,1,.3,1);
}
.nb-link.active::after { width: 52%; }

/* dropdown */
.nb-dropdown {
  position: absolute; left: 50%; top: calc(100% + 8px);
  transform: translateX(-50%) translateY(-6px);
  width: 210px;
  background: #fff; border: 1px solid #e8f0f8; border-radius: 16px;
  box-shadow: 0 16px 48px rgba(23,79,154,.13), 0 4px 12px rgba(0,0,0,.06);
  padding: .4rem;
  opacity: 0; pointer-events: none;
  transition: opacity .2s cubic-bezier(.16,1,.3,1), transform .2s cubic-bezier(.16,1,.3,1);
  z-index: 200;
}
.nb-dropdown.open {
  opacity: 1; pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}
.nb-dd-item {
  display: flex; align-items: center; gap: .5rem;
  padding: .65rem 1rem; border-radius: 10px;
  font-size: .81rem; font-weight: 600; color: #475569;
  text-decoration: none; cursor: pointer;
  transition: background .15s, color .15s;
  border: none; background: transparent; width: 100%; text-align: left;
}
.nb-dd-item:hover  { background: rgba(46,180,180,.08); color: #0f766e; }
.nb-dd-item.active { background: rgba(46,180,180,.10); color: #0f766e; }
.nb-dd-dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #2eb4b4, #174f9a);
  opacity: 0; transition: opacity .15s;
}
.nb-dd-item:hover .nb-dd-dot,
.nb-dd-item.active .nb-dd-dot { opacity: 1; }

/* CTA */
.nb-cta {
  display: inline-flex; align-items: center;
  background: linear-gradient(135deg, #2eb4b4 0%, #174f9a 100%);
  color: #fff; padding: .6rem 1.3rem; border-radius: 99px;
  font-size: .8rem; font-weight: 700; text-decoration: none;
  white-space: nowrap; flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(46,180,180,.3);
  transition: transform .2s, box-shadow .2s;
  position: relative; overflow: hidden;
}
.nb-cta:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(46,180,180,.38); }
@media (max-width: 1023px) { .nb-cta { display: none; } }

/* hamburger */
.nb-ham {
  width: 42px; height: 42px; border-radius: 12px;
  border: 1.5px solid #e2e8f0; background: #fff; color: #475569;
  display: none; place-items: center; cursor: pointer;
  transition: background .2s, border-color .2s, transform .15s;
  flex-shrink: 0;
}
.nb-ham:hover  { background: #f8fafc; border-color: #cbd5e1; }
.nb-ham:active { transform: scale(.94); }
@media (max-width: 1023px) { .nb-ham { display: grid; } }

/* ════════════════════════════════════════════════════════
   MOBILE DRAWER
   CRITICAL FIXES:
   1. z-index: 9999 — must be ABOVE .nb-shell (1000) and ALL page content
   2. The backdrop sits at z:9999 and the panel at z:10000
   3. pointer-events on the wrapper is always 'none' so it never
      blocks page interaction when closed
   4. The panel uses position:fixed (not absolute) so it is in
      the viewport stacking context, not the document flow
════════════════════════════════════════════════════════ */
.nb-drawer-wrap {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none; /* wrapper never blocks clicks when closed */
}

.nb-drawer-backdrop {
  position: absolute;
  inset: 0;
  background: transparent;
  transition: background .35s ease;
  pointer-events: none;
}
.nb-drawer-backdrop.open {
  background: rgba(15, 23, 42, .45);
  pointer-events: auto; /* only clickable when open */
}

.nb-drawer-panel {
  position: absolute;
  top: 0; right: 0; bottom: 0;
  width: min(320px, 92vw);
  background: #fff;
  box-shadow: -8px 0 48px rgba(0,0,0,.15);
  transform: translateX(110%); /* start fully off-screen */
  transition: transform .38s cubic-bezier(.16,1,.3,1);
  display: flex; flex-direction: column;
  overflow: hidden;
  pointer-events: none; /* closed = no interaction */
  z-index: 10000; /* above backdrop */
}
.nb-drawer-panel.open {
  transform: translateX(0);
  pointer-events: auto;
}

.nb-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid #f1f5f9;
  background: #fff; flex-shrink: 0;
}
.nb-drawer-close {
  width: 36px; height: 36px; border-radius: 10px;
  border: 1.5px solid #e2e8f0; background: transparent;
  display: grid; place-items: center; cursor: pointer; color: #64748b;
  transition: background .2s;
}
.nb-drawer-close:hover { background: #f8fafc; }

.nb-drawer-body {
  flex: 1; overflow-y: auto;
  padding: .75rem .9rem 1.5rem;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
.nb-drawer-body::-webkit-scrollbar { width: 3px; }
.nb-drawer-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

/* mobile contact strip */
.nb-m-contacts {
  display: flex; flex-direction: column; gap: .4rem;
  padding: .8rem .9rem; margin-bottom: .5rem;
  background: #f8fafc; border-radius: 14px;
}
.nb-m-contact-link {
  display: flex; align-items: center; gap: .5rem;
  font-size: .78rem; color: #475569; text-decoration: none;
  transition: color .2s;
}
.nb-m-contact-link:hover { color: #0f766e; }

/* mobile nav items */
.nb-m-item { margin-bottom: .15rem; }

.nb-m-link {
  display: flex; align-items: center; justify-content: space-between;
  padding: .72rem .9rem; border-radius: 12px;
  font-size: .88rem; font-weight: 600; color: #334155;
  text-decoration: none; cursor: pointer;
  transition: background .18s, color .18s;
  border: none; background: transparent; width: 100%; text-align: left;
}
.nb-m-link:hover { background: #f8fafc; color: #0f766e; }
.nb-m-link.active {
  background: linear-gradient(135deg, rgba(46,180,180,.1), rgba(23,79,154,.08));
  color: #0f766e;
}
.nb-m-chevron {
  transition: transform .25s cubic-bezier(.16,1,.3,1);
  color: #94a3b8; flex-shrink: 0;
}
.nb-m-chevron.open { transform: rotate(180deg); }

/* accordion sub-items */
.nb-m-sub {
  overflow: hidden;
  max-height: 0;
  transition: max-height .32s cubic-bezier(.16,1,.3,1), opacity .25s ease;
  opacity: 0;
}
.nb-m-sub.open { max-height: 600px; opacity: 1; }
.nb-m-sub-inner { padding: .25rem 0 .35rem .9rem; }
.nb-m-sub-item {
  display: flex; align-items: center; gap: .5rem;
  padding: .55rem .9rem; border-radius: 10px;
  font-size: .83rem; font-weight: 500; color: #64748b;
  text-decoration: none; cursor: pointer;
  transition: background .15s, color .15s;
  border: none; background: transparent; width: 100%; text-align: left;
}
.nb-m-sub-item:hover  { background: rgba(46,180,180,.07); color: #0f766e; }
.nb-m-sub-item.active { background: rgba(46,180,180,.10); color: #0f766e; }
.nb-m-sub-dot {
  width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0;
  background: linear-gradient(135deg, #2eb4b4, #174f9a);
}

.nb-drawer-footer {
  padding: .9rem 1.1rem;
  border-top: 1px solid #f1f5f9;
  flex-shrink: 0;
  background: #fff;
}
.nb-drawer-cta {
  display: block; text-align: center; width: 100%;
  background: linear-gradient(135deg, #2eb4b4 0%, #174f9a 100%);
  color: #fff; padding: .78rem; border-radius: 12px;
  font-size: .88rem; font-weight: 700; text-decoration: none;
  box-shadow: 0 4px 14px rgba(46,180,180,.28);
  transition: transform .2s, box-shadow .2s;
}
.nb-drawer-cta:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(46,180,180,.35); }
`;

/* ─── Component ─────────────────────────────────────────────── */
export default function Navbar() {
  const [scrolled,       setScrolled      ] = useState(false);
  const [mobileOpen,     setMobileOpen    ] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const [isSuperuser,    setIsSuperuser   ] = useState(false);
  const [divisions,      setDivisions     ] = useState([]);
  const [chemCategories, setChemCategories] = useState([]);

  const location    = useLocation();
  const navigate    = useNavigate();
  const closeTimer  = useRef(null);

  /* scroll watcher */
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 18);
    fn();
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  /* close drawer on route change */
  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    if (location.hash) scrollToHash(location.hash.slice(1), 120);
  }, [location.pathname, location.hash]);

  /* body scroll lock — lock/unlock when drawer opens/closes */
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow   = 'hidden';
      document.body.style.touchAction = 'none'; // prevent iOS bounce through
    } else {
      document.body.style.overflow   = '';
      document.body.style.touchAction = '';
    }
    return () => {
      document.body.style.overflow   = '';
      document.body.style.touchAction = '';
    };
  }, [mobileOpen]);

  /* auth */
  useEffect(() => {
    api.currentUser()
      .then(d => setIsSuperuser(Boolean(d.authenticated && d.is_superuser)))
      .catch(() => setIsSuperuser(false));
  }, [location.pathname]);

  /* divisions + chem categories */
  useEffect(() => {
    Promise.all([
      api.divisions({ page_size: 100 }),
      api.chemicalCategories({ page_size: 100 }),
    ]).then(([d, c]) => {
      setDivisions(d.results     || []);
      setChemCategories(c.results || []);
    }).catch(() => {});
  }, []);

  /* build nav items */
  const builtItems = navItems.map(item => {
    if (item.label === 'Divisions') return {
      ...item,
      sub: divisions.map(d => ({ label: formatNavLabel(d.tag || d.name), path: `/divisions?division=${d.id}`, hash: '' })),
    };
    if (item.label === 'Products') return {
      ...item,
      sub: chemCategories.map(c => ({ label: formatNavLabel(c.name), path: `/products?chemical_category=${c.id}`, hash: '' })),
    };
    return item;
  });
  const allItems = isSuperuser
    ? [...builtItems, { label: 'Admin', path: '/admin' }]
    : builtItems;

  /* active helpers */
  const isParentActive = item =>
    location.pathname === item.path ||
    (item.path !== '/' && location.pathname.startsWith(item.path + '/'));

  const isSubActive = sub => {
    if (sub.hash) return location.pathname === sub.path && location.hash === '#' + sub.hash;
    return location.pathname === sub.path;
  };

  /* desktop dropdown */
  const openDropdown  = label => { clearTimeout(closeTimer.current); setActiveDropdown(label); };
  const closeDropdown = ()    => { closeTimer.current = setTimeout(() => setActiveDropdown(null), 130); };

  /* hash-aware navigation */
  const handleSubClick = useCallback((sub, e) => {
    e.preventDefault();
    setActiveDropdown(null);
    setMobileOpen(false);
    if (sub.hash) {
      if (location.pathname === sub.path) {
        scrollToHash(sub.hash);
      } else {
        navigate(sub.path);
        scrollToHash(sub.hash, 360);
      }
    } else {
      navigate(sub.path);
    }
  }, [location.pathname, navigate]);

  const toggleMobileExpand = label =>
    setMobileExpanded(prev => prev === label ? null : label);

  const closeMobile = () => setMobileOpen(false);

  return (
    <>
      <style>{css}</style>

      {/* ── TOP BAR ── */}
      <div className="nb-topbar">
        <div className="nb-topbar-inner">
          <div className="nb-tb-links">
            <a href="tel:+917373038882" className="nb-tb-link">
              <Phone size={12}/><span>+91 73730 38882</span>
            </a>
            <a href="tel:+919361186300" className="nb-tb-link">
              <Phone size={12}/><span>+91 93611 86300</span>
            </a>
            <a href="tel:+917373018880" className="nb-tb-link">
              <Phone size={12}/><span>+91 73730 18880</span>
            </a>
            <a href="mailto:info@bleen.in" className="nb-tb-link">
              <Mail size={12}/><span>info@bleen.in</span>
            </a>
          </div>
          <div className="nb-tb-socials">
            {socialLinks.map(({ label, Icon, url }) => (
              <a
                key={label}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="nb-tb-social"
              >
                <Icon size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── NAV SHELL ── */}
      <nav className={`nb-shell${scrolled ? ' scrolled' : ''}`}>
        <div className="nb-inner">

          <Link to="/" className="nb-logo" aria-label="Bleen home">
            <img src={logo} alt="Bleen"/>
            <span className="nb-logo-text">Galaxy<br/>Health Care</span>
          </Link>

          {/* Desktop links */}
          <div className="nb-links">
            {allItems.map(item => (
              <div
                key={item.label}
                className="nb-item"
                onMouseEnter={() => item.sub?.length && openDropdown(item.label)}
                onMouseLeave={closeDropdown}
              >
                <Link
                  to={item.path}
                  className={`nb-link${isParentActive(item) ? ' active' : ''}`}
                  aria-haspopup={!!item.sub?.length}
                  aria-expanded={item.sub?.length ? activeDropdown === item.label : undefined}
                >
                  {item.label}
                  {item.sub?.length > 0 && (
                    <ChevronDown
                      size={13}
                      className={`nb-chevron${activeDropdown === item.label ? ' open' : ''}`}
                    />
                  )}
                </Link>

                {item.sub?.length > 0 && (
                  <div
                    className={`nb-dropdown${activeDropdown === item.label ? ' open' : ''}`}
                    onMouseEnter={() => openDropdown(item.label)}
                    onMouseLeave={closeDropdown}
                  >
                    {item.sub.map(sub => (
                      <button
                        key={sub.label}
                        className={`nb-dd-item${isSubActive(sub) ? ' active' : ''}`}
                        onClick={e => handleSubClick(sub, e)}
                      >
                        <span className="nb-dd-dot"/>
                        {sub.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Link to="/contact" className="nb-cta">Get in Touch</Link>

          {/* Hamburger */}
          <button
            type="button"
            className="nb-ham"
            onClick={() => setMobileOpen(o => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
          </button>
        </div>
      </nav>

      {/* ── MOBILE DRAWER ──────────────────────────────────────────
          The wrapper has pointer-events:none always.
          The backdrop gets pointer-events:auto only when .open.
          The panel gets pointer-events:auto only when .open.
          This means when closed, NOTHING in this tree blocks page clicks.
      ───────────────────────────────────────────────────────────── */}
      <div className="nb-drawer-wrap" role="presentation">

        {/* Backdrop — click to close */}
        <div
          className={`nb-drawer-backdrop${mobileOpen ? ' open' : ''}`}
          onClick={closeMobile}
          aria-hidden="true"
        />

        {/* Slide-in panel */}
        <div
          className={`nb-drawer-panel${mobileOpen ? ' open' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          {/* Header */}
          <div className="nb-drawer-head">
            <Link to="/" className="nb-logo" onClick={closeMobile}>
              <img src={logo} alt="Bleen" style={{ height: 36 }}/>
              <span className="nb-logo-text">Galaxy<br/>Health Care</span>
            </Link>
            <button
              className="nb-drawer-close"
              onClick={closeMobile}
              aria-label="Close menu"
            >
              <X size={18}/>
            </button>
          </div>

          {/* Scrollable body */}
          <div className="nb-drawer-body">
            {/* Contact strip */}
            <div className="nb-m-contacts">
              <a href="tel:+917373038882" className="nb-m-contact-link">
                <Phone size={12}/><span>+91 73730 38882</span>
              </a>
              <a href="tel:+919361186300" className="nb-m-contact-link">
                <Phone size={12}/><span>+91 93611 86300</span>
              </a>
              <a href="tel:+917373018880" className="nb-m-contact-link">
                <Phone size={12}/><span>+91 73730 18880</span>
              </a>
              <a href="mailto:info@bleen.in" className="nb-m-contact-link">
                <Mail size={12}/><span>info@bleen.in</span>
              </a>
            </div>

            {/* Nav items */}
            {allItems.map(item => (
              <div key={item.label} className="nb-m-item">
                {item.sub?.length > 0 ? (
                  <>
                    <button
                      className={`nb-m-link${isParentActive(item) ? ' active' : ''}`}
                      onClick={() => toggleMobileExpand(item.label)}
                      aria-expanded={mobileExpanded === item.label}
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        size={15}
                        className={`nb-m-chevron${mobileExpanded === item.label ? ' open' : ''}`}
                      />
                    </button>
                    <div className={`nb-m-sub${mobileExpanded === item.label ? ' open' : ''}`}>
                      <div className="nb-m-sub-inner">
                        {item.sub.map(sub => (
                          <button
                            key={sub.label}
                            className={`nb-m-sub-item${isSubActive(sub) ? ' active' : ''}`}
                            onClick={e => handleSubClick(sub, e)}
                          >
                            <span className="nb-m-sub-dot"/>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.path}
                    className={`nb-m-link${isParentActive(item) ? ' active' : ''}`}
                    onClick={closeMobile}
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Footer CTA */}
          <div className="nb-drawer-footer">
            <Link to="/contact" className="nb-drawer-cta" onClick={closeMobile}>
              Get in Touch
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
